/**
 * "Install 8K Brainzr" — surfaces the browser's PWA install prompt.
 *
 * - Chrome / Edge / Android: capture the `beforeinstallprompt` event the
 *   browser fires when our PWA is installable, store it, and trigger it on
 *   button click.
 * - iOS Safari has no API for this — we show step-by-step instructions
 *   instead (Share → Add to Home Screen).
 *
 * Once installed (or dismissed for the session), the button hides itself.
 */
import { Download, Share, X } from "lucide-react";
import { useEffect, useState } from "react";

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const DISMISS_KEY = "brainzr.installDismissed";

export function InstallPrompt() {
  const [evt, setEvt] = useState<BeforeInstallPromptEvent | null>(null);
  const [iosOpen, setIosOpen] = useState(false);
  const [hidden, setHidden] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem(DISMISS_KEY) === "1";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    const onPrompt = (e: Event) => {
      e.preventDefault();
      setEvt(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => setEvt(null);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  // Detect iOS — those users need the manual "Share → Add to Home Screen" path.
  const isIOS =
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.userAgent.includes("Mac") && "ontouchend" in document);
  const isStandalone =
    window.matchMedia("(display-mode: standalone)").matches ||
    (navigator as unknown as { standalone?: boolean }).standalone === true;

  if (hidden || isStandalone) return null;
  if (!evt && !isIOS) return null;

  const dismiss = () => {
    setHidden(true);
    try {
      sessionStorage.setItem(DISMISS_KEY, "1");
    } catch { /* ignore */ }
  };

  const triggerInstall = async () => {
    if (!evt) return;
    try {
      await evt.prompt();
      const { outcome } = await evt.userChoice;
      if (outcome === "accepted") setEvt(null);
      else dismiss();
    } catch {
      dismiss();
    }
  };

  return (
    <>
      <div className="fixed left-1/2 -translate-x-1/2 z-30 bottom-[calc(env(safe-area-inset-bottom)+72px)] lg:bottom-3 max-w-[92vw]">
        <div className="glass-panel rounded-pill px-3 py-1.5 flex items-center gap-2 shadow-lg">
          <img src="/icons/icon-192.png" alt="" className="w-7 h-7 rounded" />
          <div className="text-[12px] font-ui">
            <span className="text-text-primary font-medium">Install 8K Brainzr</span>
            <span className="text-text-secondary ml-2 hidden sm:inline">
              for offline-aware access on this device
            </span>
          </div>
          <button
            onClick={() => (isIOS ? setIosOpen(true) : triggerInstall())}
            className="ml-2 px-3 py-1 rounded-pill bg-accent-gold text-canvas text-[11px] font-medium uppercase tracking-[0.04em] inline-flex items-center gap-1"
          >
            {isIOS ? <Share size={12} /> : <Download size={12} />}
            Install
          </button>
          <button
            onClick={dismiss}
            className="text-text-secondary hover:text-text-primary p-1"
            aria-label="Dismiss"
          >
            <X size={14} />
          </button>
        </div>
      </div>
      {iosOpen && (
        <div
          className="fixed inset-0 z-50 bg-canvas/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setIosOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-[400px] glass-panel rounded-xl p-6 text-center"
          >
            <img
              src="/icons/icon-192.png"
              alt=""
              className="w-16 h-16 rounded-xl mx-auto mb-3"
            />
            <h2 className="font-display text-[28px] tracking-[-0.5px] text-text-primary mb-1">
              Install 8K Brainzr
            </h2>
            <p className="text-[12px] font-ui text-text-secondary mb-4">
              On iPhone / iPad, install via Safari's share menu.
            </p>
            <ol className="text-left text-[13px] font-ui text-text-primary space-y-2 mb-4">
              <li>
                <span className="text-accent-gold font-bold mr-1">1.</span>
                Tap the <Share size={12} className="inline mx-0.5" /> Share button at the bottom of Safari.
              </li>
              <li>
                <span className="text-accent-gold font-bold mr-1">2.</span>
                Scroll down and tap <strong>Add to Home Screen</strong>.
              </li>
              <li>
                <span className="text-accent-gold font-bold mr-1">3.</span>
                Confirm — the 8K Brainzr icon appears on your home screen.
              </li>
            </ol>
            <button
              onClick={() => setIosOpen(false)}
              className="w-full h-11 rounded-xl bg-accent-gold text-canvas text-[13px] font-ui font-medium uppercase tracking-[0.04em]"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
}
